import { AxiosResponse } from 'axios'
import axios from './api'

import {
  ApplicationListResponse,
  ApplicationList,
  ApplicationResponse,
  PlacementListResponse,
  PlacementList,
  PlacementResponse,
  SearchMyFamilyResponse,
  TownListResponse,
  WorkplaceDistrictListResponse,
  WorkplaceTypeListResponse,
  WorkplaceListRequest,
  WorkplaceListResponse,
  PeriodListResponse,
  CheckExemptionResponse,
  CreateApplicationInput,
  CreateApplicationResponse,
  AttendanceTypeResponse,
  CreateAttendanceInput,
  CreateAttendanceResponse,
} from './registration.types'

export type {
  ApplicationListResponse,
  ApplicationList,
  ApplicationResponse,
  PlacementListResponse,
  PlacementList,
  PlacementResponse,
  SearchMyFamilyResponse,
  TownListResponse,
  WorkplaceDistrictListResponse,
  WorkplaceTypeListResponse,
  WorkplaceListRequest,
  WorkplaceListResponse,
  PeriodListResponse,
  CheckExemptionResponse,
  CreateApplicationInput,
  CreateApplicationResponse,
}

/*
 * Sækja mínar umsóknir
 */
export const getApplicationList = async (limit: number, page: number): Promise<ApplicationListResponse> => {
  const response = await axios.get<ApplicationList[]>('/registration/application/', {
    params: {
      limit,
      page,
    },
  })

  return {
    applications: response.data,
    totalCount: parseInt(response.headers['x-total-count'], 10),
    totalPages: parseInt(response.headers['x-total-pages'], 10),
    currentLimit: parseInt(response.headers['x-current-limit'], 10),
    currentPage: parseInt(response.headers['x-current-page'], 10),
  }
}

/*
 * Sækja umsókn
 */
export const getApplication = async (id: string): Promise<AxiosResponse<ApplicationResponse>> => {
  return axios.get<ApplicationResponse>(`/registration/application/${id}/`)
}

/*
 * Sækja fjarvistartýpur
 */
export const getAttendanceTypeList = async (id: string): Promise<AxiosResponse<AttendanceTypeResponse[]>> => {
  return axios.get<AttendanceTypeResponse[]>(`/registration/placement/${id}/attendance-types/`)
}

/*
 * Eyða umsókn
 */
export const deleteApplication = async (id: string): Promise<AxiosResponse> => {
  return axios.delete(`/registration/application/${id}/`)
}

/*
 * Sækja mínar skráningar
 */
export const getPlacementList = async (limit: number, page: number, year?: string): Promise<PlacementListResponse> => {
  const response = await axios.get<PlacementList[]>('/registration/placement/', {
    params: {
      limit,
      page,
      year,
    },
  })

  return {
    placements: response.data,
    totalCount: parseInt(response.headers['x-total-count'], 10),
    totalPages: parseInt(response.headers['x-total-pages'], 10),
    currentLimit: parseInt(response.headers['x-current-limit'], 10),
    currentPage: parseInt(response.headers['x-current-page'], 10),
  }
}

/*
 * Sækja skráningu
 */
export const getPlacement = async (id: string): Promise<AxiosResponse<PlacementResponse>> => {
  return axios.get<PlacementResponse>(`/registration/placement/${id}/`)
}

/*
 * Vefþjónusta til þess að fletta upp kennitölum á þínu fjölskyldunúmeri
 */
export const searchMyFamily = async (ssn: string): Promise<AxiosResponse<SearchMyFamilyResponse>> => {
  return axios.get<SearchMyFamilyResponse>(`registration/search-my-family/`, {
    params: {
      ssn,
    },
  })
}

/*
 * Sækja sveitarfélög
 */
export const getTownList = async (): Promise<AxiosResponse<TownListResponse[]>> => {
  return axios.get<TownListResponse[]>(`/registration/town/`)
}

/*
 * Sækja hverfi
 */
export const getWorkplaceDistrictList = async (
  townId: string
): Promise<AxiosResponse<WorkplaceDistrictListResponse[]>> => {
  return axios.get<WorkplaceDistrictListResponse[]>(`/registration/workplacedistrict/`, {
    params: {
      town: townId,
    },
  })
}

/*
 * Sækja flokkun starfsstaða
 */
export const getWorkplaceTypeList = async (townId: string): Promise<AxiosResponse<WorkplaceTypeListResponse[]>> => {
  return axios.get<WorkplaceTypeListResponse[]>(`/registration/workplacetype/`, {
    params: {
      town: townId,
    },
  })
}

/*
 * Sækja starfsstaði
 */
export const getWorkplaceList = async (
  input: WorkplaceListRequest
): Promise<AxiosResponse<WorkplaceListResponse[]>> => {
  return axios.get<WorkplaceListResponse[]>(`/registration/workplace/`, {
    params: input,
  })
}

/*
 * Sækja tímabil
 */
export const getPeriodList = async (townId: string, ssn: string): Promise<AxiosResponse<PeriodListResponse[]>> => {
  return axios.get<PeriodListResponse[]>(`/registration/period/`, {
    params: {
      town: townId,
      ssn,
    },
  })
}

/**
 * Vefþjónusta til þess að sækja upplýsingar um undanþágu
 */
export const checkExemption = async (town: string, ssn: string): Promise<AxiosResponse<CheckExemptionResponse>> => {
  return axios.get<CheckExemptionResponse>(`/registration/check-exemption/`, {
    params: {
      town,
      ssn,
    },
  })
}

/**
 * Vefþjónusta til þess að skrá umsókn.
 */
export const createApplication = async (
  data: CreateApplicationInput
): Promise<AxiosResponse<CreateApplicationResponse>> => {
  return axios.post<CreateApplicationResponse>(`/registration/application/`, data)
}

/**
 * Vefþjónusta til þess að uppfæra umsókn.
 */
export const updateApplication = async (id: string, data: CreateApplicationInput): Promise<AxiosResponse> => {
  return axios.put(`/registration/application/${id}/`, data)
}

/**
 * Vefþjónusta til þess að skrá fjarvist.
 */
export const createAttendance = async (
  id: string,
  data: CreateAttendanceInput
): Promise<AxiosResponse<CreateAttendanceResponse>> => {
  return axios.post<CreateAttendanceResponse>(`/registration/placement/${id}/post-attendance-type/`, data)
}
